import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Badge, Button, Card, CardContent, Checkbox, Input, Modal } from '@components';
import { useToast } from '../../../contexts/useToast';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import {
  useCrawlWebsite,
  useCreateWebsite,
  useDeleteWebsite,
  useSelectWebsitePages,
  useWebsitePage,
  useWebsitePages,
  useWebsites,
} from '../hooks';
import { FaqApiError } from '../services/faqService';
import { KnowledgeBase, WebsitePage, WebsiteSource } from '../types';

// A page changed in the last crawl gets a "changed" mark
const CHANGED_TOLERANCE_MS = 60 * 1000;

interface WebsiteTabProps {
  knowledgeBase: KnowledgeBase;
}

export const WebsiteTab: React.FC<WebsiteTabProps> = ({ knowledgeBase }) => {
  const { t } = useTranslation();
  const { data: websites = [], isLoading, isError } = useWebsites(knowledgeBase.id);
  // The model allows several websites per knowledge base, the UI offers one (W1)
  const website = websites[0];

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('knowledgeBase.website.title')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t('knowledgeBase.website.description')}
        </p>
      </div>

      {isError ? (
        <p className="text-red-500">{t('knowledgeBase.website.loadFailed')}</p>
      ) : isLoading ? (
        <p className="text-gray-500">{t('common.loading')}</p>
      ) : website ? (
        <WebsiteDetails website={website} />
      ) : (
        <AddWebsiteForm knowledgeBaseId={knowledgeBase.id} />
      )}
    </div>
  );
};

// The backend sends error codes, e.g. "timeout" or "http_503"; other texts are shown as
// they are
const websiteErrorText = (t: TFunction, error: string) => {
  const status = /^http_(\d+)$/.exec(error)?.[1];
  if (status && !['403', '404'].includes(status)) {
    return t('knowledgeBase.website.errors.http', { status });
  }
  return t(`knowledgeBase.website.errors.${error}`, { defaultValue: error });
};

const AddWebsiteForm: React.FC<{ knowledgeBaseId: string }> = ({ knowledgeBaseId }) => {
  const { t } = useTranslation();
  const createWebsite = useCreateWebsite(knowledgeBaseId);
  const [rootUrl, setRootUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const value = rootUrl.trim();
    try {
      // Customers often leave out the scheme
      await createWebsite.mutateAsync(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    } catch (err) {
      setError(
        err instanceof FaqApiError
          ? websiteErrorText(t, err.fieldErrors.root_url || err.message)
          : t('knowledgeBase.website.addFailed')
      );
    }
  };

  return (
    <Card>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            id="website-root-url"
            label={t('knowledgeBase.website.rootUrl')}
            placeholder="https://www.ihre-firma.de"
            value={rootUrl}
            onChange={e => setRootUrl(e.target.value)}
            helperText={t('knowledgeBase.website.rootUrlHelp')}
            errorText={error ?? undefined}
          />
          <Button
            type="submit"
            variant="gradient-multi"
            disabled={!rootUrl.trim() || createWebsite.isPending}
            className="flex items-center gap-2"
          >
            <span className="material-icons">language</span>
            {t('knowledgeBase.website.import')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const WebsiteDetails: React.FC<{ website: WebsiteSource }> = ({ website }) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const isCrawling = website.crawl_status === 'crawling';
  const { data: pages = [] } = useWebsitePages(website.id, isCrawling, website.last_crawled_at);
  const crawlWebsite = useCrawlWebsite();
  const deleteWebsite = useDeleteWebsite();
  const selectPages = useSelectWebsitePages();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [previewPageId, setPreviewPageId] = useState<string | null>(null);

  const pendingPages = pages.filter(page => page.selection === 'pending');
  const decidedPages = pages.filter(page => page.selection !== 'pending');
  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleString(i18n.language) : '';

  const handleCrawl = async () => {
    try {
      await crawlWebsite.mutateAsync(website.id);
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.website.crawlFailed') });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteWebsite.mutateAsync(website.id);
      showToast({ type: 'success', message: t('knowledgeBase.website.deleted') });
      setIsDeleteOpen(false);
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.website.deleteFailed') });
    }
  };

  const select = async (included: string[], excluded: string[]) => {
    try {
      await selectPages.mutateAsync({ websiteId: website.id, included, excluded });
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.website.selectionFailed') });
    }
  };

  return (
    <>
      <Card>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="min-w-0">
              <a
                href={website.root_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary-600 dark:text-primary-400 break-all hover:underline"
              >
                {website.root_url}
              </a>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                <CrawlStatusText
                  website={website}
                  formatDate={formatDate}
                  pendingCount={pendingPages.length}
                  isFirstImport={decidedPages.length === 0}
                />
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCrawl}
                disabled={isCrawling || crawlWebsite.isPending}
                className="flex items-center gap-1"
              >
                <span className="material-icons text-base">refresh</span>
                {t('knowledgeBase.website.recrawl')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsDeleteOpen(true)}
                className="p-1.5 h-8 w-8"
                title={t('knowledgeBase.website.delete')}
              >
                <span className="material-icons text-base text-red-600">delete</span>
              </Button>
            </div>
          </div>
          {pages.length > 0 && pages.every(page => page.content_length === 0) && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              {t('knowledgeBase.website.noContent')}
            </p>
          )}
        </CardContent>
      </Card>

      {pendingPages.length > 0 && (
        <PendingPagesSelection
          pages={pendingPages}
          isFirstImport={decidedPages.length === 0}
          onConfirm={select}
          onPreview={setPreviewPageId}
          isSaving={selectPages.isPending}
        />
      )}

      {decidedPages.length > 0 && (
        <Card>
          <CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
              {t('knowledgeBase.website.pages')}
            </h3>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {decidedPages.map(page => (
                <PageRow
                  key={page.id}
                  page={page}
                  website={website}
                  onPreview={() => setPreviewPageId(page.id)}
                  onInclude={() => select([page.id], [])}
                  onExclude={() => select([], [page.id])}
                  isSaving={selectPages.isPending}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <PagePreviewModal pageId={previewPageId} onClose={() => setPreviewPageId(null)} />

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title={t('knowledgeBase.website.delete')}
        question={t('knowledgeBase.website.confirmDelete')}
        itemName={website.root_url}
        warning={t('knowledgeBase.website.deleteWarning')}
        confirmLabel={t('knowledgeBase.website.delete')}
        isDeleting={deleteWebsite.isPending}
      />
    </>
  );
};

const CrawlStatusText: React.FC<{
  website: WebsiteSource;
  formatDate: (value: string | null) => string;
  pendingCount: number;
  isFirstImport: boolean;
}> = ({ website, formatDate, pendingCount, isFirstImport }) => {
  const { t } = useTranslation();
  if (website.crawl_status === 'crawling') return <>{t('knowledgeBase.website.crawling')}</>;
  if (website.crawl_status === 'failed') {
    // A failed re-crawl keeps the pages of the last successful one active
    return website.last_crawled_at ? (
      <span className="text-amber-700 dark:text-amber-300">
        {t('knowledgeBase.website.recrawlFailed', {
          error: websiteErrorText(t, website.last_error),
          date: formatDate(website.last_crawled_at),
        })}
      </span>
    ) : (
      <span className="text-red-600 dark:text-red-400">
        {t('knowledgeBase.website.importFailed', {
          error: websiteErrorText(t, website.last_error),
        })}
      </span>
    );
  }
  // Until the customer applies the selection, the pages are not used
  if (pendingCount > 0) {
    return (
      <span className="font-medium text-info-700 dark:text-info-300">
        {isFirstImport
          ? t('knowledgeBase.website.pendingFirstImport')
          : t('knowledgeBase.website.pendingNewPages', { count: pendingCount })}
      </span>
    );
  }
  if (website.last_crawled_at) {
    return (
      <>{t('knowledgeBase.website.lastImport', { date: formatDate(website.last_crawled_at) })}</>
    );
  }
  return <>{t('knowledgeBase.website.waiting')}</>;
};

interface PendingPagesSelectionProps {
  pages: WebsitePage[];
  isFirstImport: boolean;
  onConfirm: (included: string[], excluded: string[]) => Promise<void>;
  onPreview: (pageId: string) => void;
  isSaving: boolean;
}

const PendingPagesSelection: React.FC<PendingPagesSelectionProps> = ({
  pages,
  isFirstImport,
  onConfirm,
  onPreview,
  isSaving,
}) => {
  const { t } = useTranslation();
  // Only the pages the customer changed; the others follow the suggestion
  const [choices, setChoices] = useState<Record<string, boolean>>({});
  const isChecked = (page: WebsitePage) => choices[page.id] ?? page.is_suggested;

  const checkedCount = pages.filter(isChecked).length;

  const handleConfirm = () =>
    onConfirm(
      pages.filter(isChecked).map(page => page.id),
      pages.filter(page => !isChecked(page)).map(page => page.id)
    );

  // Above and below the list: with many pages the lower one is out of view
  const confirmButton = (
    <Button
      variant="gradient-multi"
      onClick={handleConfirm}
      disabled={isSaving}
      className="shrink-0"
    >
      {t('knowledgeBase.website.confirmPages', { count: checkedCount })}
    </Button>
  );

  return (
    <Card className="border-2 !border-info-300 dark:!border-info-700">
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {isFirstImport
                ? t('knowledgeBase.website.selectPages')
                : t('knowledgeBase.website.newPages', { count: pages.length })}
            </h3>
            <p className="text-sm text-info-800 dark:text-info-200 mt-1">
              {t('knowledgeBase.website.selectPagesHelp')}
            </p>
          </div>
          {confirmButton}
        </div>
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {pages.map(page => (
            <li key={page.id} className="flex items-center justify-between gap-3 py-2">
              <Checkbox
                id={`website-page-${page.id}`}
                checked={isChecked(page)}
                onChange={e => setChoices({ ...choices, [page.id]: e.target.checked })}
                label={page.title || page.url}
                helperText={page.title ? page.url : undefined}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPreview(page.id)}
                className="shrink-0"
              >
                {t('knowledgeBase.website.preview')}
              </Button>
            </li>
          ))}
        </ul>
        {confirmButton}
      </CardContent>
    </Card>
  );
};

interface PageRowProps {
  page: WebsitePage;
  website: WebsiteSource;
  onPreview: () => void;
  onInclude: () => void;
  onExclude: () => void;
  isSaving: boolean;
}

const PageRow: React.FC<PageRowProps> = ({
  page,
  website,
  onPreview,
  onInclude,
  onExclude,
  isSaving,
}) => {
  const { t } = useTranslation();
  const isIncluded = page.selection === 'included';
  const changedInLastCrawl =
    !!page.changed_at &&
    !!website.last_crawled_at &&
    new Date(page.changed_at).getTime() >=
      new Date(website.last_crawled_at).getTime() - CHANGED_TOLERANCE_MS;

  return (
    <li className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3">
      <div className="min-w-0">
        <p
          className={`font-medium break-words ${
            isIncluded ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {page.title || page.url}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 break-all">{page.url}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {isIncluded ? (
            <Badge
              variant={
                page.index_status === 'indexed'
                  ? 'success'
                  : page.index_status === 'failed'
                    ? 'error'
                    : 'info'
              }
              size="sm"
            >
              {t(`knowledgeBase.website.indexStatus.${page.index_status}`)}
            </Badge>
          ) : (
            <Badge variant="secondary" size="sm">
              {t('knowledgeBase.website.excluded')}
            </Badge>
          )}
          {changedInLastCrawl && (
            <Badge variant="warning" size="sm">
              {t('knowledgeBase.website.changed')}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="ghost" size="sm" onClick={onPreview}>
          {t('knowledgeBase.website.preview')}
        </Button>
        {/* Colour and icon tell the two actions apart in long lists */}
        {isIncluded && page.index_status !== 'failed' ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onExclude}
            disabled={isSaving}
            className="flex items-center gap-1 !text-red-600 dark:!text-red-400"
          >
            <span className="material-icons text-base">remove_circle_outline</span>
            {t('knowledgeBase.website.exclude')}
          </Button>
        ) : (
          // Including again also retries a failed page
          <Button
            variant="outline"
            size="sm"
            onClick={onInclude}
            disabled={isSaving}
            className="flex items-center gap-1 !text-green-700 dark:!text-green-400"
          >
            <span className="material-icons text-base">
              {isIncluded ? 'refresh' : 'add_circle_outline'}
            </span>
            {isIncluded ? t('knowledgeBase.website.retry') : t('knowledgeBase.website.include')}
          </Button>
        )}
      </div>
    </li>
  );
};

const PagePreviewModal: React.FC<{ pageId: string | null; onClose: () => void }> = ({
  pageId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { data: page, isLoading } = useWebsitePage(pageId);

  return (
    <Modal isOpen={pageId !== null} onClose={onClose} size="lg">
      <div className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white break-words">
              {page?.title || page?.url || t('knowledgeBase.website.preview')}
            </h2>
            {page && <p className="text-xs text-gray-500 break-all">{page.url}</p>}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1 h-8 w-8 shrink-0">
            <span className="material-icons">close</span>
          </Button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('knowledgeBase.website.previewHelp')}
        </p>
        <div className="max-h-[60vh] overflow-y-auto rounded-lg bg-gray-50 p-4 text-sm text-gray-700 whitespace-pre-line break-words dark:bg-gray-800 dark:text-gray-300">
          {isLoading || !page
            ? t('common.loading')
            : page.content || t('knowledgeBase.website.pageWithoutContent')}
        </div>
      </div>
    </Modal>
  );
};
